import {
  Box,
  Button,
  Divider,
  Flex,
  FormControl,
  FormLabel,
  Heading,
  Input,
  Link,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Select,
  Stack,
  Text,
  VStack,
  useDisclosure,
  useToast,
} from '@chakra-ui/react'
import { useCallback, useEffect, useState } from 'react'
import { useDispatch } from 'react-redux'
import { Link as RouterLink } from 'react-router-dom'
import TimezoneSelect from 'react-timezone-select'

import {
  useDeleteUserMutation,
  useLazyGetUserProfileQuery,
  usePostUserProfileMutation,
} from '../../app/services/api'
import { updateUser } from '../../features/auth/authSlice'
import { useAuth } from '../../hooks/useAuth'
import { maybeChangeLocale, useLocale } from '../../hooks/useLocale'
import { useLogout } from '../../hooks/useLogout'
import { PostProfileRequest, User } from '../../types/api'
import { useMixpanel } from '../../utils/MixpanelContext'
import { useFeatureFlagEnabled } from 'posthog-js/react'

export const DISPLAY_NEW_LANGUAGE_OPTIONS_FLAG_KEY =
  'display_new_language_options'

const UserProfile: React.FC = () => {
  const { user } = useAuth()
  if (!user) throw new Error('User is not defined')
  const { t, i18n } = useLocale()
  const [triggerGetUserProfile, { data: userProfileData }] =
    useLazyGetUserProfileQuery()
  const [postUserProfile] = usePostUserProfileMutation()
  const [isButtonDisabled, setIsButtonDisabled] = useState<boolean>(true)
  const [selectedTimezone, setSelectedTimezone] = useState<any>(
    Intl.DateTimeFormat().resolvedOptions().timeZone
  )
  const mixpanel = useMixpanel()
  const toast = useToast()
  const dispatch = useDispatch()
  const [deleteUser] = useDeleteUserMutation()
  const {
    isOpen: isDeleteAlertOpen,
    onOpen: onDeleteAlertOpen,
    onClose: onDeleteAlertClose,
  } = useDisclosure()
  const onLogout = useLogout()

  useEffect(() => {
    mixpanel.track('profile_edit_open')
    triggerGetUserProfile()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const [formState, setFormState] = useState<PostProfileRequest>({
    name: '',
    timezone: '',
    interfaceLanguage: '',
  })

  const featureFlagEnabled = useFeatureFlagEnabled(
    DISPLAY_NEW_LANGUAGE_OPTIONS_FLAG_KEY
  )
  const displayNewLanguageOptionsEnabled =
    process.env.NODE_ENV === 'development' || featureFlagEnabled

  useEffect(() => {
    setIsButtonDisabled(
      !(formState?.name && formState?.timezone && formState?.interfaceLanguage)
    )
  }, [formState])

  const onReset = useCallback(() => {
    const changes = {
      name: userProfileData?.profile.name,
      timezone: userProfileData?.timezone ?? selectedTimezone,
      interfaceLanguage:
        userProfileData?.interfaceLanguage ?? userProfileData?.language,
    }
    Object.entries(changes).forEach(([key, value]) =>
      setFormState((prev: any) => ({ ...prev, [key]: value }))
    )
    if (userProfileData?.timezone) {
      setSelectedTimezone(userProfileData?.timezone)
    }
  }, [userProfileData, selectedTimezone])

  const onDelete = async () => {
    try {
      await deleteUser({}).unwrap()
      toast({
        status: 'success',
        title: t('success'),
        description: t('account_delete_success'),
        isClosable: true,
      })
      mixpanel.track('account_delete_success')
      onLogout()
    } catch (error: any) {
      mixpanel.track('account_delete_fail', { error: JSON.stringify(error) })
      reportError(error)
      toast({
        status: 'error',
        title: t('error'),
        description: error?.data?.message || t('there_was_an_error'),
        isClosable: true,
      })
    }
  }

  useEffect(() => {
    onReset()
  }, [onReset])

  const handleChange = (event: any) => {
    const {
      target: { name, value },
    } = event
    setFormState((prev) => ({ ...prev, [name]: value }))
  }

  const handleTimezoneChange = (timezone: any) => {
    const name = 'timezone'
    const value = timezone.value
    setFormState((prev) => ({ ...prev, [name]: value }))
  }

  const onSubmit = useCallback(
    async (event: any) => {
      event.preventDefault()
      try {
        const user = await postUserProfile(formState).unwrap()
        toast({
          status: 'success',
          title: t('success'),
          description: t('your_profile_is_updated'),
          isClosable: true,
        })
        maybeChangeLocale(
          user.interfaceLanguage ?? user.language!,
          i18n.language
        )
        dispatch(updateUser(user as User))
        mixpanel.track('profile_edit_success')
      } catch (err: any) {
        toast({
          status: 'error',
          title: t('error'),
          description: err?.data?.message || t('there_was_an_error'),
          isClosable: true,
        })
        mixpanel.track('profile_edit_fail', { message: err?.data?.message })
      }
    },
    [formState, postUserProfile, t, dispatch, mixpanel, toast, i18n]
  )

  if (!userProfileData) return null

  return (
    <>
      <form id="profile-form" onSubmit={onSubmit}>
        <Flex minH={'50vh'} align={'top'} justify={'center'} bg={'gray.50'}>
          <Stack
            spacing={4}
            w={'full'}
            maxW={'md'}
            bg={'white'}
            rounded={'xl'}
            boxShadow={'lg'}
            p={6}
            my={12}
          >
            <Heading lineHeight={1.1} fontSize={{ base: '2xl', sm: '3xl' }}>
              {t('edit_profile')}
            </Heading>
            <FormControl id="name">
              <FormLabel>{t('name')}</FormLabel>
              <Input
                value={formState.name}
                name="name"
                _placeholder={{ color: 'gray.500' }}
                type="text"
                onChange={handleChange}
              />
            </FormControl>
            <FormControl id="email">
              <FormLabel>{t('email')}</FormLabel>
              <Input type="text" disabled value={user.email} />
            </FormControl>
            <FormControl id="timezone">
              <FormLabel>{t('timezone')}</FormLabel>
              <TimezoneSelect
                name="timezone"
                value={formState.timezone}
                onChange={handleTimezoneChange}
              />
            </FormControl>
            <FormControl id="interfaceLanguage">
              <FormLabel>{t('language')}</FormLabel>
              <Select
                name="interfaceLanguage"
                value={formState.interfaceLanguage}
                onChange={handleChange}
              >
                <option value="en">English</option>
                <option value="ko">한국어</option>
                {displayNewLanguageOptionsEnabled ? (
                  <>
                    <option value="ja">日本語</option>
                    <option value="zh">中文（简体）</option>
                    <option value="tw">中文（繁體）</option>
                    <option value="ru">Русский</option>
                  </>
                ) : null}
              </Select>
            </FormControl>
            <Link
              as={RouterLink}
              to="/password_forgot"
              color="blue.500"
              textDecor="underline"
            >
              {t('forgot_password')}
            </Link>

            <Stack spacing={6} direction={['column', 'row']}>
              <Button colorScheme="red" w="full" onClick={onReset}>
                {t('reset')}
              </Button>
              <Button
                colorScheme="green"
                w="full"
                disabled={isButtonDisabled}
                onClick={onSubmit}
              >
                {t('save')}
              </Button>
            </Stack>
            <VStack>
              <Divider mb={4} />
              <FormLabel>{t('delete_account_description')}</FormLabel>
              <Box style={{ paddingTop: 10 }}>
                <Button
                  onClick={onDeleteAlertOpen}
                  bg={'primary.500'}
                  color={'white'}
                >
                  {t('delete_account')}
                </Button>
              </Box>
            </VStack>
          </Stack>
        </Flex>
      </form>
      <Modal isCentered isOpen={isDeleteAlertOpen} onClose={onDeleteAlertClose}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader> {t('account_deletion')} </ModalHeader>
          <Divider />
          <ModalBody>
            <Text fontSize="18px">{t('delete_account_confirmation')}</Text>
          </ModalBody>
          <ModalFooter>
            <Button onClick={onDeleteAlertClose} mr={5}>
              {t('cancel')}
            </Button>
            <Button onClick={onDelete} colorScheme="primary" type="submit">
              {t('confirm_deletion')}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </>
  )
}

export default UserProfile
